import { Injectable, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { StoragePrefix } from '@core/constant';
import { environment } from '@env/environment';
import { StorageService } from 'src/app/utils';

@Injectable({ providedIn: 'root' })
export class IdleTimeoutService {
  private readonly IDLE_TIME = environment.idleTime * 60 * 1000;
  private readonly STORAGE_KEY = 'last_activity_ts';
  private readonly LOCK_KEY = 'idle_locked';
  private readonly PERSIST_INTERVAL_MS = 30_000;
  private intervalId: any;
  private lastActivityMs = Date.now();
  private lastPersistedMs = 0;

  isIdle = signal(false);

  private router = inject(Router);
  private _storage = inject(StorageService);

  startWatching() {
    if (this.getDecoded(this.LOCK_KEY) === '1') {
      this.isIdle.set(true);
      return;
    }

    const stored = this.getDecoded(this.STORAGE_KEY);
    this.lastActivityMs = stored ? Number(stored) : Date.now();
    this.lastPersistedMs = this.lastActivityMs;

    if (!localStorage.getItem(this.STORAGE_KEY)) {
      this.persistActivity(true);
    }

    ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'].forEach((event) => {
      window.addEventListener(event, this.onActivity, { passive: true });
    });

    this.stopWatching();
    this.intervalId = setInterval(() => this.checkIdleTime(), 5000);

    this.checkIdleTime();
  }

  stopWatching() {
    clearInterval(this.intervalId);
  }

  private onActivity = () => {
    if (this.isIdle()) return;
    this.lastActivityMs = Date.now();
    this.persistActivity(false);
  };

  private persistActivity(force: boolean) {
    const now = Date.now();
    if (!force && now - this.lastPersistedMs < this.PERSIST_INTERVAL_MS) {
      return;
    }
    this.lastPersistedMs = now;
    this.setEncoded(this.STORAGE_KEY, String(this.lastActivityMs));
  }

  private getLastActivity(): number {
    return this.lastActivityMs || Date.now();
  }

  private checkIdleTime() {
    const idleDuration = Date.now() - this.getLastActivity();

    if (idleDuration >= this.IDLE_TIME) {
      this.setEncoded(this.LOCK_KEY, '1');
      this.isIdle.set(true);
      this.stopWatching();
    }
  }

  confirmLogout() {
    this.cleanup();
  }

  continueSession() {
    localStorage.removeItem(this.LOCK_KEY);
    this.lastActivityMs = Date.now();
    this.persistActivity(true);
    this.isIdle.set(false);
    this.startWatching();
  }

  onLogout() {
    this.stopWatching();
    ['mousemove', 'keydown', 'click', 'scroll', 'touchstart'].forEach((event) => {
      window.removeEventListener(event, this.onActivity as any);
    });
    this.isIdle.set(false);
  }
  public cleanup() {
    this.onLogout();
    const lang = this._storage.get(StoragePrefix.LANGUAGE);
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.LOCK_KEY);
    localStorage.clear();
    this._storage.set(StoragePrefix.LANGUAGE, lang);
    window.location.href = '/login';
  }

  private setEncoded(key: string, value: string) {
    localStorage.setItem(key, btoa(value));
  }

  private getDecoded(key: string): string | null {
    const value = localStorage.getItem(key);
    return value ? atob(value) : null;
  }
}
